import { MAP_COLOR_THEME_STYLE } from "@app/shared/map-appearance/constants";
import { MapColorThemeContextProps } from "@app/shared/map-appearance/interfaces";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QESButton } from "@app/shared/qes-standards/Button";
import React, { Context, FC, useContext, useEffect, useRef, useState } from "react";

interface MapColorSlotEditorProps {
  colorThemeContext: Context<MapColorThemeContextProps | null>;
  slotIndex: number;
}

export const MapColorSlotEditor: FC<MapColorSlotEditorProps> = ({ colorThemeContext, slotIndex }) => {
  const ctx = useContext(colorThemeContext);
  if (!ctx) throw new Error("MapColorSlotEditor must be used within a MapColorThemeContextProvider");

  const slotDef = ctx.slotDefinitions[slotIndex];
  const [color, setColor] = useState<string>(MAP_COLOR_THEME_STYLE.DEFAULT_SWATCH_COLOR);

  useEffect(() => {
    if (ctx.themeToEdit && slotDef) {
      setColor(ctx.themeToEdit.colors[slotDef.key] ?? MAP_COLOR_THEME_STYLE.DEFAULT_SWATCH_COLOR);
    }
  }, [ctx.themeToEdit, slotDef]);

  const handleEditColor = (newColor: string) => {
    if (!ctx.themeToEdit || !slotDef) return;
    setColor(newColor);
    ctx.setThemeToEdit({
      ...ctx.themeToEdit,
      colors: { ...ctx.themeToEdit.colors, [slotDef.key]: newColor },
    });
  };

  const colorInputRef = useRef<HTMLInputElement>(null);
  const isSelected = ctx.editingSlotIndex === slotIndex;
  const pickerId = `map-appearance__color-picker--${ctx.themeToEdit?.uuid}-${slotIndex}`;

  return (
    <>
      <div className="map-appearance__swatch-container">
        <div className="map-appearance__swatch">
          {slotDef?.label}
          <div className="map-appearance__swatch-colors">
            <div
              className={`map-appearance__color-display${isSelected ? " selected" : ""}`}
              style={{ "--map-swatch-color": color } as React.CSSProperties}
            />
          </div>
        </div>
        <QESButton
          onClick={() => {
            ctx.setEditingSlotIndex(slotIndex);
            colorInputRef.current?.click();
          }}
          qesTestId={`map-appearance__slot-editor-${ctx.themeToEdit?.uuid}-${slotIndex}`}
          qesType="secondary"
        >
          <div className="map-appearance__edit-button-content">
            <QesIcon iconName={QesIcons.PencilV1} isDecorative />
            Edit
          </div>
        </QESButton>
      </div>
      <input
        className="map-appearance__color-picker-input"
        data-testid={pickerId}
        onChange={(e) => handleEditColor(e.target.value)}
        ref={colorInputRef}
        type="color"
        value={color}
      />
    </>
  );
};
